import { CommonModule, formatCurrency } from '@angular/common';
import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { UIChart } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../shared/components/card/card/card';
import { EmptyChart } from '../../../../shared/components/empty-chart/empty-chart';
import { DashboardService } from '../../../../services/dashboard.service';
import { barChartDefaultOptions } from '../../../../interfaces/charts';
import type { DepositDailyMetricsItem, DepositDailyMetricsMeta } from '../../../../interfaces/dashboard';
import type { ChartConfiguration } from 'chart.js';
import { finalize } from 'rxjs';
import dayjs from 'dayjs';

@Component({
  selector: 'app-dashboard-deposit-avg-chart',
  imports: [
    CommonModule,
    Card,
    CardHeader,
    UIChart,
    EmptyChart,
    ProgressSpinnerModule,
    SkeletonModule,
    ButtonModule,
    DatePickerModule,
    FormsModule,
  ],
  templateUrl: './dashboard-deposit-avg-chart.html',
  styleUrl: './dashboard-deposit-avg-chart.css',
})
export class DashboardDepositAvgChart {
  private readonly _dashboardService: DashboardService = inject(DashboardService);

  public loading: WritableSignal<boolean> = signal<boolean>(false);
  public chartData: WritableSignal<any> = signal<any>(null);
  public chartOptions: WritableSignal<ChartConfiguration['options']> = signal<ChartConfiguration['options']>({
    ...barChartDefaultOptions,
  });
  public meta: WritableSignal<DepositDailyMetricsMeta | null> = signal<DepositDailyMetricsMeta | null>(null);
  public selectedDate: Date = dayjs().toDate();

  private _sortedData: DepositDailyMetricsItem[] = [];

  public ngOnInit(): void {
    this.getDepositDailyMetrics();
  }

  public getDepositDailyMetrics(): void {
    this.loading.set(true);

    const filters = {
      date: dayjs(this.selectedDate).format('YYYY-MM-DD'),
    };

    this._dashboardService
      .depositDailyMetrics(filters)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data?.length) {
            this.meta.set(response.meta);
            this._mountChartData(response.data);
            this._mountChartOptions();
          }
        },
      });
  }

  private _extractDateStr(isoDate: string): string {
    return isoDate.substring(0, 10);
  }

  private _formatDateLabel(isoDate: string): string {
    const dateStr = this._extractDateStr(isoDate);
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}`;
  }

  private _mountChartData(data: DepositDailyMetricsItem[]): void {
    this._sortedData = [...data].sort(
      (a, b) => this._extractDateStr(a.date_dep).localeCompare(this._extractDateStr(b.date_dep))
    );

    const labels = this._sortedData.map((item) => this._formatDateLabel(item.date_dep));
    const avgValues = this._sortedData.map((item) => item.avg_dep);

    this.chartData.set({
      labels,
      datasets: [
        {
          label: 'Ticket Médio (R$)',
          data: avgValues,
          borderWidth: 2,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          borderRadius: 6,
          backgroundColor: '#3B82F699',
          borderColor: '#3B82F6',
        },
      ],
    });
  }

  private _mountChartOptions(): void {
    const sortedDataRef = this._sortedData;

    this.chartOptions.set({
      ...barChartDefaultOptions,
      maintainAspectRatio: false,
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: {
          ...barChartDefaultOptions?.plugins?.legend,
          display: true,
        },
        tooltip: {
          ...barChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            title: (items: any) => {
              return items[0]?.label ? `Data: ${items[0].label}` : '';
            },
            label: (context: any) => {
              const idx = context.dataIndex;
              const item = sortedDataRef[idx];
              if (!item) return '';
              return [
                `Quantidade: ${item.qty_deps.toLocaleString('pt-BR')}`,
                `Valor Total: ${formatCurrency(item.value_deps, 'pt_BR', 'R$')}`,
                `Ticket Médio: ${formatCurrency(item.avg_dep, 'pt_BR', 'R$')}`,
              ];
            },
          },
        },
      },
      scales: {
        ...barChartDefaultOptions?.scales,
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          grid: { display: true },
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return `R$ ${value}`;
            },
          },
        },
      },
    });
  }
}
